/**
 * Canonical profile-handle (slug) validator — the ONE source of truth for
 * validating `professional_profiles.handle` before it reaches the database.
 *
 * Mirrors the DB function `public.normalize_and_check_handle(raw text)`
 * (baseline.sql), which is invoked by the `sync_professional_profile_handle`
 * BEFORE trigger on every `professional_profiles.handle` write (fired by the
 * `atomic_profile_update` RPC on profile save). That function RAISEs on an
 * invalid handle:
 *
 *   - length 3–50 (after trim + lowercase)
 *   - `^[a-z0-9]([a-z0-9-]{1,48}[a-z0-9])?$` (lowercase a-z, 0-9, hyphens;
 *     no leading or trailing hyphen)
 *   - no consecutive hyphens (`--`)
 *   - not a reserved handle (checked app-side via `isReservedProfileSlug`
 *     at the consumer callsite — that helper lives in the app, not here)
 *
 * BAL-600 (Sentry BALLEE-FJ): before this schema existed, every editor
 * surface used a loose `/^[a-z0-9-]+$/` regex with no minimum length and no
 * anchor-to-alphanumeric. That accepted values like `"-2"` (2 chars, leading
 * hyphen), which slipped past client + server validation and threw an
 * uncaught `ServiceError: handle must be 3 to 50 characters (got -2)` from the
 * DB trigger — surfacing as a 500 in Sentry instead of a friendly field error.
 * Every handle-save surface now consumes THIS validator so an invalid handle
 * returns a validation error, never a thrown ServiceError.
 */
import { z } from 'zod';

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 50;

/**
 * Structural regex mirroring the DB check exactly: must start and end with an
 * alphanumeric, hyphens only in the interior. (Consecutive hyphens are
 * rejected separately so the message can be specific.)
 */
export const HANDLE_REGEX = /^[a-z0-9]([a-z0-9-]{1,48}[a-z0-9])?$/;

export const HANDLE_ERROR_MESSAGE =
  'Profile slug must be 3–50 characters: lowercase letters, numbers, and single hyphens (not at the start or end)';

/**
 * True when `value` would pass the DB `normalize_and_check_handle` structural
 * checks (reserved-handle check excluded — that is an app-level concern).
 * Assumes `value` is already trimmed + lowercased by the caller/schema.
 */
export function isValidProfileHandle(value: string): boolean {
  return (
    value.length >= HANDLE_MIN_LENGTH &&
    value.length <= HANDLE_MAX_LENGTH &&
    HANDLE_REGEX.test(value) &&
    !value.includes('--')
  );
}

/**
 * Required profile-handle validator. Trims + lowercases (as the DB does), then
 * enforces the full DB contract. Use where a handle is mandatory (wizard
 * Step 1 identity). Callers may chain `.refine(isReservedProfileSlug)` for the
 * app-level reserved-handle check.
 */
export const profileHandleSchema = z
  .string()
  .transform((val) => val.trim().toLowerCase())
  .refine((val) => val.length >= HANDLE_MIN_LENGTH, {
    message: HANDLE_ERROR_MESSAGE,
  })
  .refine((val) => val.length <= HANDLE_MAX_LENGTH, {
    message: 'Profile slug too long',
  })
  .refine((val) => HANDLE_REGEX.test(val), { message: HANDLE_ERROR_MESSAGE })
  .refine((val) => !val.includes('--'), {
    message: 'Profile slug cannot contain consecutive hyphens',
  });

/**
 * Optional profile-handle validator. An empty/whitespace value normalizes to
 * `undefined` (the DB then auto-generates or leaves the slug unchanged); any
 * non-empty value is trimmed + lowercased and fully validated against the DB
 * contract. Use on `/edit` + `/visibility` surfaces where the field may be
 * left blank.
 */
export const optionalProfileHandleSchema = z
  .string()
  .optional()
  .transform((val) => {
    const normalized = (val ?? '').trim().toLowerCase();
    return normalized === '' ? undefined : normalized;
  })
  .refine((val) => val === undefined || isValidProfileHandle(val), {
    message: HANDLE_ERROR_MESSAGE,
  });
